import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable'; // The default

import KeyboardButton from './KeyboardButton';
import KeyboardWrapper from './KeyboardWrapper';

import LatinLayout from './layouts/LatinLayout';
import CyrillicLayout from './layouts/CyrillicLayout';
import SymbolsLayout from './layouts/SymbolsLayout';
import GermanLayout from './layouts/GermanLayout';
import EstonianLayout from './layouts/EstonianLayout';
import { NumpadNumberLayout, NumpadUtilityKeys } from './layouts/NumpadLayout';

import BackspaceIcon from './icons/BackspaceIcon';
import LanguageIcon from './icons/LanguageIcon';
import ShiftIcon from './icons/ShiftIcon';
import DraggableIcon from './icons/DraggableIcon';

const ENTER_KEY_VALUE = '⎆';
const HIDE_KEY_VALUE = '↧';

export default class Keyboard extends PureComponent {
  static propTypes = {
    inputNode: PropTypes.any.isRequired,
    onClick: PropTypes.func,
    isFirstLetterUppercase: PropTypes.bool,
    defaultKeyboard: PropTypes.any,
    secondaryKeyboard: PropTypes.string,
    hideKeyboard: PropTypes.func,
    opacity: PropTypes.number,
    isDraggable: PropTypes.bool,
    dataset: PropTypes.any,
    class: PropTypes.string,
    handleBackspaceClick: PropTypes.func,
    handleEnterClick: PropTypes.func
  };

  static defaultProps = {
    rightButtons: [],
    isFirstLetterUppercase: true,
    isDraggable: true,
    defaultKeyboard: 'us',
    dataset: { type: 'input' }
  };

  constructor(props) {
    super(props);
    this.keyboardRef = React.createRef();
    this.handleLetterButtonClick = this.handleLetterButtonClick.bind(this);
    this.handleBackspaceClick = props.handleBackspaceClick ? props.handleBackspaceClick : this.handleBackspaceClick.bind(this);
    this.handleEnterClick = props.handleEnterClick ? props.handleEnterClick : this.handleEnterClick.bind(this);
    this.clearInput = this.clearInput.bind(this);
    this.handleShiftClick = this.handleShiftClick.bind(this);
    this.handleSymbolsClick = this.handleSymbolsClick.bind(this);
    this.handleLanguageClick = this.handleLanguageClick.bind(this);
    this.handleDragKeyClick = this.handleDragKeyClick.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleContainerPointerDown = this.handleContainerPointerDown.bind(this);
    this.handleContainerPointerUp = this.handleContainerPointerUp.bind(this);
    this.state = {
      currentLanguage: props.defaultKeyboard,
      showSymbols: false,
      uppercase: this.isUppercase(),
      dragPosition: { x: 0, y: 0 },
      dragEnabled: false
    };
  }

  positionKeyboardNearInput() {
    const { inputNode } = this.props;
    const keyboardElement = this.keyboardRef.current;

    if (!inputNode || !keyboardElement || typeof window === 'undefined') {
      return false;
    }

    let inputRect;
    try {
      inputRect = inputNode.getBoundingClientRect();
    } catch (err) {
      console.error(err);
      return false;
    }

    if (!inputRect || (inputRect.width === 0 && inputRect.height === 0)) {
      return false;
    }

    const viewportHeight = window.innerHeight || 0;
    const thresholdRatio = 0.65;
    const thresholdY = viewportHeight * thresholdRatio;

    if (inputRect.bottom < thresholdY) {
      return false;
    }

    const bounds = this.getDragBounds();
    if (!bounds) {
      return false;
    }

    const { baseLeft = 0, baseTop = 0 } = bounds;
    const keyboardWidth = keyboardElement.offsetWidth || 0;
    const keyboardHeight = keyboardElement.offsetHeight || 0;
    const viewportWidth = window.innerWidth || 0;
    const margin = 16;
    const gap = 12;

    let targetLeft = inputRect.left + inputRect.width / 2 - keyboardWidth / 2;
    targetLeft = Math.min(viewportWidth - margin - keyboardWidth, Math.max(margin, targetLeft));

    const spaceBelow = viewportHeight - inputRect.bottom;
    let targetTop;
    if (spaceBelow >= keyboardHeight + gap + margin) {
      targetTop = inputRect.bottom + gap;
    } else {
      targetTop = inputRect.top - keyboardHeight - gap;
    }
    targetTop = Math.min(viewportHeight - margin - keyboardHeight, Math.max(margin, targetTop));

    const desiredPosition = { x: targetLeft - baseLeft, y: targetTop - baseTop };
    const clampedPosition = this.clampPositionToBounds(desiredPosition, bounds);
    const currentPosition = this.state.dragPosition || { x: 0, y: 0 };

    if (Math.abs(currentPosition.x - clampedPosition.x) < 0.5 && Math.abs(currentPosition.y - clampedPosition.y) < 0.5) {
      return false;
    }

    this.setState({ dragPosition: clampedPosition });

    return true;
  }

  componentDidMount() {
    this.ensurePositionWithinBounds();
    this.positionKeyboardNearInput();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleWindowResize);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.inputNode && this.props.inputNode !== prevProps.inputNode) {
      this.positionKeyboardNearInput();
    }

    if (
      prevProps.inputNode !== this.props.inputNode ||
      prevState.showSymbols !== this.state.showSymbols ||
      prevState.currentLanguage !== this.state.currentLanguage ||
      prevState.dragEnabled !== this.state.dragEnabled ||
      prevProps.uppercase !== this.props.uppercase ||
      prevProps.dataset !== this.props.dataset
    ) {
      this.ensurePositionWithinBounds();
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleWindowResize);
    }
  }

  isNumpadLayout() {
    const { inputNode } = this.props;

    if (!inputNode) {
      return false;
    }

    const datasetLayout = inputNode.dataset?.keyboardLayout || inputNode.dataset?.keyboardType;
    const datasetType = inputNode.dataset?.type;
    if (typeof datasetLayout === 'string' && datasetLayout.toLowerCase() === 'numpad') {
      return true;
    }

    if (typeof datasetType === 'string' && ['number', 'numeric', 'decimal'].includes(datasetType.toLowerCase())) {
      return true;
    }

    const type = (inputNode.type || '').toLowerCase();
    if (type === 'number') {
      return true;
    }

    const inputMode = (inputNode.inputMode || '').toLowerCase();
    return inputMode === 'numeric' || inputMode === 'decimal';
  }

  getKeys() {
    let keysSet;
    if (this.state.showSymbols) {
      keysSet = SymbolsLayout;
    } else if (this.state.currentLanguage === 'de') {
      keysSet = GermanLayout;
    } else if (this.state.currentLanguage === 'ru') {
      keysSet = CyrillicLayout;
    } else if (this.state.currentLanguage === 'et') {
      keysSet = EstonianLayout;
    } else {
      keysSet = LatinLayout;
    }

    return this.props.uppercase ? keysSet.map((keyRow) => keyRow.map((key) => key.toUpperCase())) : keysSet;
  }

  getSymbolsKeyValue() {
    let symbolsKeyValue;
    if (!this.state.showSymbols) {
      symbolsKeyValue = '.?!&';
    } else if (this.state.currentLanguage === 'us' || this.state.currentLanguage === 'de') {
      symbolsKeyValue = 'Abc';
    } else if (this.state.currentLanguage === 'ru') {
      symbolsKeyValue = 'Абв';
    } else {
      symbolsKeyValue = 'Abc';
    }
    return symbolsKeyValue;
  }

  handleLanguageClick() {
    this.setState({
      currentLanguage: this.state.currentLanguage === this.props.defaultKeyboard ? this.props.secondaryKeyboard : this.props.defaultKeyboard
    });
  }

  clearInput() {
    const { inputNode } = this.props;

    inputNode.value = '';
    if (this.props.onClick) {
      this.props.onClick('');
    }

    setTimeout(() => {
      inputNode.focus();
    }, 0);
    inputNode.dispatchEvent(new CustomEvent('input'));
  }

  handleShiftClick() {
    // this.setState({ uppercase: !this.state.uppercase });

    this.props.setUppercaseKeyboard(!this.props.uppercase);

    setTimeout(() => {
      this.props.inputNode.focus();
    });
  }

  handleSymbolsClick() {
    this.setState({ showSymbols: !this.state.showSymbols });
  }

  handleLetterButtonClick(key) {
    const { inputNode } = this.props;
    const { value } = inputNode;
    let selectionStart;
    let selectionEnd;
    try {
      selectionStart = inputNode.selectionStart;
      selectionEnd = inputNode.selectionEnd;
    } catch (e) {
      selectionStart = 0;
      selectionEnd = value.length;
    }

    const nextValue = `${value.slice(0, selectionStart)}${key}${value.slice(selectionEnd)}`;
    // const nextSelectionPosition = selectionStart + (key.length || 1);

    inputNode.value = nextValue;
    if (this.props.onClick) {
      this.props.onClick(nextValue);
    }

    setTimeout(() => {
      inputNode.focus();
      try {
        const offset = !isFinite(key) ? key.length : 1;
        inputNode.setSelectionRange(selectionStart + offset, selectionStart + offset);
      } catch (err) {
        console.error(err);
      }
    }, 0);
    this.setState({ uppercase: this.isUppercase() });
    inputNode.dispatchEvent(new CustomEvent('input'));
  }

  handleDragKeyClick() {
    this.setState(
      (prevState) => {
        const nextEnabled = !prevState.dragEnabled;
        return { dragEnabled: nextEnabled };
      },
      () => {
        if (!this.state.dragEnabled && this.props.inputNode) {
          setTimeout(() => {
            try {
              this.props.inputNode.focus();
            } catch (err) {
              console.error(err);
            }
          }, 0);
        }
      }
    );
  }

  handleDrag(event, data) {
    const bounds = this.getDragBounds();
    const position = this.clampPositionToBounds({ x: data.x, y: data.y }, bounds);
    this.setState({ dragPosition: position });
  }

  handleDragStop(event, data) {
    const bounds = this.getDragBounds();
    const position = this.clampPositionToBounds({ x: data.x, y: data.y }, bounds);
    this.setState({ dragPosition: position });
  }

  isUppercase() {
    const { inputNode, isFirstLetterUppercase, dataset } = this.props;

    if (inputNode == null || inputNode.type == null) {
      return false;
    }

    return inputNode.type !== 'password' && dataset.type !== 'email' && !inputNode.value.length && isFirstLetterUppercase;
  }

  handleBackspaceClick() {
    const { inputNode } = this.props;
    const { value } = inputNode;
    let selectionStart;
    let selectionEnd;
    try {
      selectionStart = inputNode.selectionStart;
      selectionEnd = inputNode.selectionEnd;
    } catch (e) {
      selectionStart = 0;
      selectionEnd = value.length;
    }

    let nextValue;
    let nextSelectionPosition;
    if (selectionStart === selectionEnd) {
      nextValue = value.substring(0, selectionStart - 1) + value.substring(selectionEnd);
      nextSelectionPosition = selectionStart - 1;
    } else {
      nextValue = value.substring(0, selectionStart) + value.substring(selectionEnd);
      nextSelectionPosition = selectionStart;
    }
    nextSelectionPosition = nextSelectionPosition > 0 ? nextSelectionPosition : 0;

    inputNode.value = nextValue;
    if (this.props.onClick) {
      this.props.onClick(nextValue);
    }

    setTimeout(() => {
      inputNode.focus();
      try {
        inputNode.setSelectionRange(nextSelectionPosition, nextSelectionPosition);
      } catch (err) {
        console.error(err);
      }
    }, 0);
    this.setState({ uppercase: this.isUppercase() });
    inputNode.dispatchEvent(new CustomEvent('input'));
  }

  // basically tab - switch between fields
  handleEnterClick() {
    const { inputNode } = this.props;

    if (!inputNode) {
      return;
    }

    const focusableSelector =
      'input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([disabled])';
    const focusableElements = Array.from(document.querySelectorAll(focusableSelector)).filter((element) => {
      const isHidden = element.offsetParent === null || element.closest('[aria-hidden="true"]');
      return !isHidden;
    });

    const currentIndex = focusableElements.indexOf(inputNode);

    if (currentIndex === -1) {
      return;
    }

    const nextElement = focusableElements.slice(currentIndex + 1).find((element) => !element.disabled);

    if (!nextElement) {
      return;
    }

    nextElement.focus();

    setTimeout(() => {
      try {
        if (typeof nextElement.setSelectionRange === 'function') {
          const valueLength = nextElement.value ? nextElement.value.length : 0;
          nextElement.setSelectionRange(valueLength, valueLength);
        } else if (typeof nextElement.select === 'function') {
          nextElement.select();
        }
      } catch (err) {
        console.error(err);
      }
    }, 0);
  }

  handleWindowResize() {
    this.ensurePositionWithinBounds();
    this.positionKeyboardNearInput();
  }

  ensurePositionWithinBounds() {
    const bounds = this.getDragBounds();
    if (!bounds) {
      return;
    }
    const currentPosition = this.state.dragPosition || { x: 0, y: 0 };
    const clamped = this.clampPositionToBounds(currentPosition, bounds);
    if (clamped.x !== currentPosition.x || clamped.y !== currentPosition.y) {
      this.setState({ dragPosition: clamped });
    }
  }

  clampPositionToBounds(position, bounds) {
    if (!bounds) {
      return position;
    }
    const clampedX = Math.min(bounds.right, Math.max(bounds.left, position.x));
    const clampedY = Math.min(bounds.bottom, Math.max(bounds.top, position.y));
    return { x: clampedX, y: clampedY };
  }

  getDragBounds() {
    if (!this.keyboardRef.current || typeof window === 'undefined') {
      return null;
    }

    const element = this.keyboardRef.current;
    const width = element.offsetWidth || 0;
    const height = element.offsetHeight || 0;
    const viewportWidth = window.innerWidth || 0;
    const viewportHeight = window.innerHeight || 0;
    const viewportMargin = 16;
    const bottomOffset = 20;

    const baseLeft = Math.max((viewportWidth - width) / 2, 0);
    const baseTop = Math.max(viewportHeight - height - bottomOffset, 0);

    let minX = viewportMargin - baseLeft;
    let maxX = viewportWidth - viewportMargin - baseLeft - width;

    if (minX > maxX) {
      const centerX = (minX + maxX) / 2;
      minX = centerX;
      maxX = centerX;
    }

    let minY = viewportMargin - baseTop;
    let maxY = viewportHeight - viewportMargin - baseTop - height;

    if (minY > maxY) {
      const centerY = (minY + maxY) / 2;
      minY = centerY;
      maxY = centerY;
    }

    return { left: minX, right: maxX, top: minY, bottom: maxY, baseLeft, baseTop };
  }

  handleContainerPointerDown(event) {
    if (event.target?.closest('.keyboard-button')) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (this.keyboardRef.current) {
      try {
        this.keyboardRef.current.focus();
      } catch (err) {
        console.error(err);
      }
    }

    const { inputNode } = this.props;
    setTimeout(() => {
      if (inputNode) {
        try {
          inputNode.focus();
        } catch (err) {
          console.error(err);
        }
      }
    }, 0);
  }

  handleContainerPointerUp(event) {
    if (event.target?.closest('.keyboard-button')) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
  }

  renderAlphaNumericKeyboard() {
    const { inputNode, secondaryKeyboard } = this.props;
    const keys = this.getKeys();
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
    const symbolsKeyValue = this.getSymbolsKeyValue();
    const datasetType = inputNode?.dataset?.type || inputNode?.type;
    const handleEnterClick = this.props.handleEnterClick || this.handleEnterClick;
    const dragButtonClasses = `keyboard-drag-toggle${this.state.dragEnabled ? ' active' : ''}`;

    return (
      <>
        <div className="keyboard-row">
          {numbers.map((button) => (
            <KeyboardButton value={button} onClick={this.handleLetterButtonClick} classes={'keyboard-numberButton'} key={button} />
          ))}
          <KeyboardButton value={<BackspaceIcon />} onClick={this.handleBackspaceClick} />
        </div>

        {keys.map((row, i) => (
          <div key={`r${i}`} className="keyboard-row">
            {keys.length === i + 1 && <KeyboardButton classes="shift-symbols" value={<ShiftIcon />} onClick={this.handleShiftClick} />}
            {row.map((button, ii) => (
              <KeyboardButton value={button} onClick={this.handleLetterButtonClick} key={`b${ii}`} />
            ))}

            {keys.length === i + 1 && <KeyboardButton classes="shift-symbols" value={symbolsKeyValue} onClick={this.handleSymbolsClick} />}
          </div>
        ))}

        <div className="keyboard-row">
          {typeof secondaryKeyboard !== 'undefined' ? <KeyboardButton value={<LanguageIcon />} onClick={this.handleLanguageClick} /> : null}
          {datasetType === 'email' ? <KeyboardButton value={'@'} onClick={this.handleLetterButtonClick} /> : null}
          {this.props.isDraggable !== false ? <KeyboardButton value={<DraggableIcon />} classes={dragButtonClasses} onClick={this.handleDragKeyClick} /> : null}
          <KeyboardButton value={' '} classes="keyboard-space" onClick={this.handleLetterButtonClick} />
          {datasetType === 'email' ? <KeyboardButton value={'.'} onClick={this.handleLetterButtonClick} /> : null}
          <KeyboardButton value={ENTER_KEY_VALUE} onClick={handleEnterClick} />
          <KeyboardButton value={HIDE_KEY_VALUE} classes="keyboard-submit-button" onClick={this.props.hideKeyboard} />
        </div>
      </>
    );
  }

  renderNumpadKeyboard() {
    const handleEnterClick = this.props.handleEnterClick || this.handleEnterClick;
    const numberKeys = NumpadNumberLayout.flat();

    return (
      <div className="keyboard-numpad">
        <div className="keyboard-numpad-main">
          {numberKeys.map((keyValue, index) =>
            keyValue == null ? (
              <span className="keyboard-button keyboard-numpad-spacer" key={`numpad-spacer-${index}`} aria-hidden="true"></span>
            ) : (
              this.renderNumpadKey(keyValue, `numpad-main-${index}`, handleEnterClick)
            )
          )}
        </div>
        <div className="keyboard-numpad-side">{NumpadUtilityKeys.map((keyValue, index) => this.renderNumpadKey(keyValue, `numpad-util-${index}`, handleEnterClick))}</div>
      </div>
    );
  }

  renderNumpadKey(keyValue, key, handleEnterClick) {
    switch (keyValue) {
      case 'BACKSPACE':
        return <KeyboardButton value={<BackspaceIcon />} classes="keyboard-utilButton" onClick={this.handleBackspaceClick} key={key} />;
      case 'CLEAR':
        return <KeyboardButton value={'C'} classes="keyboard-utilButton" onClick={this.clearInput} key={key} />;
      case 'DRAG':
        return <KeyboardButton value={<DraggableIcon />} classes={`keyboard-utilButton keyboard-drag-toggle${this.state.dragEnabled ? ' active' : ''}`} onClick={this.handleDragKeyClick} key={key} />;
      case 'ENTER':
        return <KeyboardButton value={ENTER_KEY_VALUE} classes="keyboard-utilButton" onClick={handleEnterClick} key={key} />;
      case 'HIDE':
        return <KeyboardButton value={HIDE_KEY_VALUE} classes="keyboard-submit-button" onClick={this.props.hideKeyboard} key={key} />;
      default:
        return <KeyboardButton value={keyValue} classes="keyboard-numberButton" onClick={this.handleLetterButtonClick} key={key} />;
    }
  }

  render() {
    const isNumpad = this.isNumpadLayout();
    const containerClassName = `${this.props.class ? this.props.class : 'keyboard keyboard-wrapper'}${isNumpad ? ' keyboard-numpad-size' : ''}`;
    const dragPosition = this.state.dragPosition || { x: 0, y: 0 };
    const isDragDisabled = this.props.isDraggable === false || !this.state.dragEnabled;
    const bounds = this.getDragBounds();

    return (
      <KeyboardWrapper>
        <div className="keyboardVisible">
          <Draggable disabled={isDragDisabled} position={dragPosition} onDrag={this.handleDrag} onStop={this.handleDragStop} cancel=".keyboard-drag-toggle" bounds={bounds || undefined}>
            <div
              ref={this.keyboardRef}
              className={containerClassName}
              tabIndex={-1}
              onMouseDown={this.handleContainerPointerDown}
              onMouseUp={this.handleContainerPointerUp}
              onClick={this.handleContainerPointerUp}
              onTouchStart={this.handleContainerPointerDown}
              onTouchEnd={this.handleContainerPointerUp}
              style={{
                opacity: `${typeof this.props.opacity !== 'undefined' ? this.props.opacity : 1}`
              }}>
              {isNumpad ? this.renderNumpadKeyboard() : this.renderAlphaNumericKeyboard()}
            </div>
          </Draggable>
        </div>
      </KeyboardWrapper>
    );
  }
}
